import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { CardQuotaRequestDetail } from '../../../../../core/models/card-quota/card-quota-request.model';
import { ClientQuotaListRow } from '../../../../../core/models/card-quota/client-quota.model';
import { ClientIInfo } from '../../../../../core/models/parking.model';
import { MapperCardQuotaRequestService } from '../../../../../core/mappers/card-quota/mapper-card-quota-request.service';
import { MapperClientQuotaService } from '../../../../../core/mappers/card-quota/mapper-client-quota.service';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';

@Component({
  selector: 'app-modal-quota-request',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-quota-request.component.html',
  styleUrl: './modal-quota-request.component.css',
})
export class ModalQuotaRequestComponent implements OnInit {
  @Input() requestId = 0;
  @Input() siteId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  formData!: CardQuotaRequestDetail;
  requestRemark = '';
  clients: ClientIInfo[] = [];
  targetQuotas: ClientQuotaListRow[] = [];
  isLoading = signal(false);

  requestTypes = [
    { value: 'increase', label: 'ขอเพิ่ม' },
    { value: 'decrease', label: 'ขอลด' },
    { value: 'suspend', label: 'ระงับ' },
    { value: 'resume', label: 'ยกเลิกระงับ' },
  ];
  quotaLevels = [
    { value: 'master', label: 'ฝ่ายอาคาร (โควต้าหลัก)' },
    { value: 'sub', label: 'ร้านค้า (โควต้าย่อย)' },
  ];
  cardScopes = [
    { value: 'free', label: 'บัตรฟรี' },
    { value: 'expiry', label: 'บัตรมีหมดอายุ' },
  ];
  suspendModes = [
    { value: 'block_new_only', label: 'ห้ามออกบัตรใหม่ (บัตรเดิมยังใช้ได้ แต่แก้ไขไม่ได้)' },
    { value: 'suspend_all_cards', label: 'ระงับบัตรทั้งหมด + คืนโควต้า' },
    { value: 'not_ready_only', label: 'ระงับสถานะ Platform + คืนโควต้า (ไม่กระทบวันหมดอายุที่ลาน)' },
  ];

  get needsTargetQuota(): boolean {
    return ['decrease', 'suspend', 'resume'].includes(this.formData.requestType);
  }

  get isSuspend(): boolean {
    return this.formData.requestType === 'suspend';
  }

  private readonly api = inject(ApiCardQuotaService);
  private readonly clientScope = inject(ClientAccessScopeService);
  private readonly mapper = inject(MapperCardQuotaRequestService);
  private readonly clientQuotaMapper = inject(MapperClientQuotaService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isClientFieldLocked(): boolean {
    return this.clientScope.isClientLocked();
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyForm(this.siteId);
    this.loadClients();
    if (this.requestId > 0) {
      this.loadDetail(this.requestId);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onRequestTypeChange(): void {
    if (!this.needsTargetQuota) {
      this.formData.targetQuotaId = null;
      this.formData.suspendCardMode = null;
    }
    this.loadTargetQuotas();
  }

  onClientChange(): void {
    this.loadTargetQuotas();
  }

  onSaveDraft(): void {
    this.save(false);
  }

  onSubmitApproval(): void {
    this.save(true);
  }

  private save(submitForApproval: boolean): void {
    if (!this.formData.clientId) {
      this.toast.error('กรุณาเลือก Client');
      return;
    }
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }
    if (this.formData.requestType === 'increase' && this.formData.requestedQty <= 0) {
      this.toast.error('จำนวนที่ขอต้องมากกว่า 0');
      return;
    }
    if (this.needsTargetQuota && !this.formData.targetQuotaId) {
      this.toast.error('กรุณาเลือกโควต้าเป้าหมาย');
      return;
    }
    if (this.isSuspend && !this.formData.suspendCardMode) {
      this.toast.error('กรุณาเลือกโหมดระงับ');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const isEdit = this.formData.id > 0;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const payload = this.mapper.mapToPayload(
      { ...this.formData, siteId: this.siteId, requestRemark: this.requestRemark },
      userId,
      menuId,
      submitForApproval
    );

    this.isLoading.set(true);
    this.api.saveCardQuotaRequest(payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapSaveResponse(res);
        this.toast.success(
          submitForApproval
            ? mapped.requiresApproval
              ? 'ส่งอนุมัติเรียบร้อย'
              : 'อนุมัติอัตโนมัติเรียบร้อย'
            : 'บันทึก draft เรียบร้อย'
        );
        this.saveSuccess.emit();
        this.close.emit();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'บันทึกไม่สำเร็จ');
        this.isLoading.set(false);
      },
    });
  }

  private loadDetail(id: number): void {
    this.isLoading.set(true);
    this.api.getCardQuotaRequestById(id).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapDetail(res as CardQuotaRequestDetail & Record<string, unknown>);
        this.loadTargetQuotas();
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.isLoading.set(false);
      },
    });
  }

  private loadClients(): void {
    const userId = Number(this.cookieService.get('user_id') || 0);
    if (!this.siteId || !userId) {
      this.clients = [];
      return;
    }

    this.clientScope.load(this.siteId, userId).subscribe({
      next: (state) => {
        this.clients = state.clients;
        if (this.isClientFieldLocked && this.clients.length === 1) {
          this.formData.clientId = this.clients[0].id;
          this.loadTargetQuotas();
        }
      },
      error: () => {
        this.clients = [];
      },
    });
  }

  private loadTargetQuotas(): void {
    if (!this.siteId || !this.formData.clientId || !this.needsTargetQuota) {
      this.targetQuotas = [];
      return;
    }
    this.api.getClientQuotasForTable(this.siteId, 1, 100, this.formData.clientId).subscribe({
      next: (res) => {
        this.targetQuotas = this.clientQuotaMapper.mapTable(res).items;
      },
      error: () => (this.targetQuotas = []),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
